import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MapPin, Briefcase, Mail, ArrowUpRight, ArrowRight } from "lucide-react";
import { BD_PARTNER } from "@/data/bdPartner";

const CANONICAL =
  "https://notionminds.sa/careers/business-development-partner-saudi-arabia";
const PAGE_TITLE =
  "Independent Business Development Partner – Saudi Arabia | Notion Minds Systems";
const PAGE_DESCRIPTION =
  "Notion Minds Systems is looking for experienced Independent Business Development Partners in Saudi Arabia with established enterprise technology networks and relationships.";
const OG_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/371a705d-1e9e-4b80-8780-1f565747a945/images/c5032847b278a2bb23ffa25ee713faaedfc057a31ce7ee29c2acdb0aa9356abc.png";
const LOGO =
  "https://customer-assets.emergentagent.com/job_enterprise-transform-6/artifacts/i7xup0t7_NMS_LOGO.png";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Notion Minds Systems",
  alternateName: "مؤسسة نوش مايندز سيستمز",
  url: "https://notionminds.sa",
  logo: LOGO,
  email: "hello@notionminds.sa",
  telephone: "+966 50 694 7879",
  address: {
    "@type": "PostalAddress",
    streetAddress: "F-7/S03968, Floor 1, Building 7036, Saida Street, Durayhmiah District",
    addressLocality: "Riyadh",
    postalCode: "12796",
    addressCountry: "SA",
  },
};

const webPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  url: CANONICAL,
  inLanguage: "en",
  isPartOf: {
    "@type": "WebSite",
    url: "https://notionminds.sa",
    name: "Notion Minds Systems",
  },
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://notionminds.sa/" },
    { "@type": "ListItem", position: 2, name: "Careers", item: "https://notionminds.sa/careers" },
    {
      "@type": "ListItem",
      position: 3,
      name: "Independent Business Development Partner – Saudi Arabia",
      item: CANONICAL,
    },
  ],
};

const jobJsonLd = {
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: "Independent Business Development Partner – Saudi Arabia",
  description:
    "Notion Minds Systems is expanding its business development network in Saudi Arabia and is looking for experienced Independent Business Development Partners with established relationships within the Saudi enterprise and technology market. This is an Independent / Commission-Based Partnership — not a salaried employment position. The partner identifies and develops new business opportunities, leverages existing professional relationships with senior decision-makers (CIOs, CTOs, IT Directors, Procurement), and earns commission on qualified business originated through their direct efforts.",
  datePosted: "2026-07-02",
  employmentType: "CONTRACTOR",
  hiringOrganization: {
    "@type": "Organization",
    name: "Notion Minds Systems",
    sameAs: "https://notionminds.sa",
    logo: LOGO,
  },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Riyadh",
      addressCountry: "SA",
    },
  },
};

export default function BDPartner() {
  return (
    <div data-testid="bd-partner-page">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Notion Minds Systems" />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(jobJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="pt-24 pb-10 max-w-[1440px] mx-auto px-6 lg:px-12">
        <nav className="text-xs font-mono-accent text-white/40 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#d4af37]">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/careers" className="hover:text-[#d4af37]">Careers</Link>
          <span className="mx-2">/</span>
          <span className="text-[#d4af37]">Business Development Partner</span>
        </nav>
        <p className="font-mono-accent text-xs text-[#d4af37] mb-5">
          [ Careers · Business Development ]
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08] max-w-4xl">
          Independent Business Development Partner –{" "}
          <span className="text-gradient-gold font-semibold">Saudi Arabia</span>
        </h1>
        <div className="flex flex-wrap gap-6 mt-6 text-sm text-white/60">
          <span className="inline-flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#d4af37]" /> {BD_PARTNER.location}
          </span>
          <span className="inline-flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#d4af37]" /> {BD_PARTNER.type}
          </span>
        </div>
        <p className="mt-8 text-white/75 text-lg leading-relaxed max-w-3xl">
          Notion Minds Systems is expanding its business development network and
          is looking for an experienced{" "}
          <strong className="text-white font-medium">Independent Business Development Partner in Saudi Arabia</strong>{" "}
          with established relationships across the Saudi enterprise and
          technology market.
        </p>
        <p className="mt-4 text-white/60 text-base leading-relaxed max-w-3xl">
          This IT business development opportunity in Saudi Arabia is ideal for
          experienced IT sales professionals, technology consultants and
          business development specialists who can open doors to qualified
          organizations and senior decision-makers.
        </p>
        <a
          href={`mailto:${BD_PARTNER.applyEmail}?subject=${encodeURIComponent("Application: " + BD_PARTNER.title)}`}
          data-testid="bd-partner-apply-hero"
          className="mt-8 inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-4 font-semibold hover:bg-[#e5c059] transition-colors"
        >
          <Mail className="w-4 h-4" /> Apply at {BD_PARTNER.applyEmail}
        </a>
      </section>

      {/* SECTIONS */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-16 space-y-14">
        {BD_PARTNER.sections.map((sec, i) => (
          <div key={sec.heading || i} data-testid={`bd-section-${i}`} className="border-t border-white/10 pt-10">
            <h2 className="font-display text-2xl lg:text-3xl font-light mb-6 text-white">
              {sec.heading}
            </h2>
            {sec.paragraphs?.map((p, pi) => (
              <p key={pi} className="text-white/70 text-base leading-relaxed max-w-3xl mb-4">
                {p}
              </p>
            ))}
            {sec.chips && (
              <div className="flex flex-wrap gap-2 my-5">
                {sec.chips.map((c) => (
                  <span
                    key={c}
                    className="text-xs font-mono-accent text-[#d4af37] border border-[#d4af37]/40 px-3 py-1.5"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
            {sec.after && (
              <p className="text-white/70 text-base leading-relaxed max-w-3xl mt-4">{sec.after}</p>
            )}
            {sec.groups && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 my-5">
                {sec.groups.map((g) => (
                  <div key={g.name} className="p-5 bg-[#0d0d12] border border-[#1c1c22] hover:border-[#d4af37]/40 transition-colors">
                    <p className="font-display text-base font-medium text-white mb-2">{g.name}</p>
                    <p className="text-xs text-white/55 leading-relaxed">{g.items}</p>
                  </div>
                ))}
              </div>
            )}
            {sec.bullets && (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 my-5 max-w-5xl">
                {sec.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/70 leading-relaxed">
                    <span className="w-1.5 h-1.5 bg-[#d4af37] mt-2 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </section>

      {/* HOW TO APPLY + INTERNAL LINKS */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">
        <div className="relative overflow-hidden border border-[#d4af37]/30 p-10 lg:p-14 grid-pattern-fine">
          <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/8 via-transparent to-transparent" />
          <div className="relative max-w-3xl">
            <h2 className="font-display text-3xl lg:text-4xl font-light mb-6">How to Apply</h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Please submit your CV / professional profile along with a brief
              introduction covering: your experience in the Saudi market;
              sectors and organizations you have worked with; your area of
              technology sales or business development expertise; the nature of
              your existing enterprise relationships; and examples of business
              opportunities you have originated.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={`mailto:${BD_PARTNER.applyEmail}?subject=${encodeURIComponent("Application: " + BD_PARTNER.title)}`}
                data-testid="bd-partner-apply-bottom"
                className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-4 font-semibold hover:bg-[#e5c059] transition-colors"
              >
                Apply at {BD_PARTNER.applyEmail} <ArrowUpRight className="w-4 h-4" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-4 font-medium hover:border-white/60 hover:bg-white/5 transition-all"
              >
                Contact us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <Link to="/services" className="text-white/60 hover:text-[#d4af37] transition-colors">
                Explore our IT services in Saudi Arabia
              </Link>
              <Link to="/about" className="text-white/60 hover:text-[#d4af37] transition-colors">
                About Notion Minds Systems
              </Link>
              <Link to="/careers" className="text-white/60 hover:text-[#d4af37] transition-colors">
                All careers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
